import { TransformationResult, FieldTransformation, TransformationContext, TransformerOptions } from './types';

/**
 * Base transformer class providing common transformation functionality
 * All specific transformers should extend this class
 */
export abstract class BaseTransformer<TInput, TOutput> {
  protected fieldTransformations: FieldTransformation<TInput, TOutput>[] = [];
  protected options: TransformerOptions;

  constructor(options: TransformerOptions = {}) {
    this.options = { 
      strict: false, 
      skipInvalid: false, 
      ...options 
    };
  }

  /**
   * Add a field transformation
   */
  addFieldTransformation(transformation: FieldTransformation<TInput, TOutput>): this {
    this.fieldTransformations.push(transformation);
    return this;
  }

  /**
   * Add multiple field transformations
   */
  addFieldTransformations(transformations: FieldTransformation<TInput, TOutput>[]): this {
    this.fieldTransformations.push(...transformations);
    return this;
  }

  /**
   * Transform a single input to output
   */
  transform(input: TInput, context?: TransformationContext): TransformationResult<TOutput> {
    try {
      const output = this.performTransformation(input, context);
      return {
        success: true,
        data: output
      };
    } catch (error) {
      return {
        success: false,
        errors: [error instanceof Error ? error.message : 'Transformation failed']
      };
    }
  }

  /**
   * Transform an array of inputs to outputs
   */
  transformArray(inputs: TInput[], context?: TransformationContext): TransformationResult<TOutput[]> {
    const results: TOutput[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];

    for (let i = 0; i < inputs.length; i++) {
      const result = this.transform(inputs[i], context);
      
      if (result.success && result.data) {
        results.push(result.data);
      } else {
        const errorMsg = `Item ${i + 1}: ${result.errors?.join(', ') || 'Transformation failed'}`;
        
        if (this.options.skipInvalid) {
          warnings.push(errorMsg);
        } else {
          errors.push(errorMsg);
        }
      }
    }

    if (errors.length > 0 && !this.options.skipInvalid) {
      return {
        success: false,
        errors
      };
    }

    return {
      success: true,
      data: results,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Perform the actual transformation logic
   * Subclasses should override this method
   */
  protected abstract performTransformation(input: TInput, context?: TransformationContext): TOutput;

  /**
   * Helper method to apply field transformations
   */
  protected applyFieldTransformations(input: TInput, context?: TransformationContext): Partial<TOutput> {
    const output: Partial<TOutput> = {};

    for (const transformation of this.fieldTransformations) {
      const sourceValue = this.getSourceValue(input, transformation.sourceField);
      
      if (sourceValue === undefined || sourceValue === null) {
        if (transformation.required && this.options.strict) {
          throw new Error(`Required field '${String(transformation.sourceField)}' is missing`);
        }
        
        if (transformation.defaultValue !== undefined) {
          this.setTargetValue(output, transformation.targetField, transformation.defaultValue);
        }
        continue;
      }

      const transformedValue = transformation.transform 
        ? transformation.transform(sourceValue, context)
        : sourceValue;

      this.setTargetValue(output, transformation.targetField, transformedValue);
    }

    return output;
  }

  /**
   * Get value from source object using field path
   */
  private getSourceValue(source: TInput, fieldPath: keyof TInput | string): unknown {
    if (typeof fieldPath === 'string' && fieldPath.includes('.')) {
      return this.getNestedValue(source as unknown as Record<string, unknown>, fieldPath);
    }
    return (source as Record<string, unknown>)[fieldPath as string];
  }

  /**
   * Set value in target object using field path
   */
  private setTargetValue(target: Partial<TOutput>, fieldPath: keyof TOutput | string, value: unknown): void {
    if (typeof fieldPath === 'string' && fieldPath.includes('.')) {
      this.setNestedValue(target as unknown as Record<string, unknown>, fieldPath, value);
    } else {
      (target as Record<string, unknown>)[fieldPath as string] = value;
    }
  }

  /**
   * Get nested value using dot notation (e.g., 'user.profile.name')
   */
  private getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current: Record<string, unknown> | unknown, key) => 
      current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined, obj);
  }

  /**
   * Set nested value using dot notation
   */
  private setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((current: Record<string, unknown>, key) => {
      if (!current[key]) current[key] = {};
      return current[key] as Record<string, unknown>;
    }, obj);
    target[lastKey] = value;
  }

  /**
   * Create a copy of this transformer with new options
   */
  withOptions(options: Partial<TransformerOptions>): this {
    const newTransformer = Object.create(Object.getPrototypeOf(this));
    newTransformer.fieldTransformations = [...this.fieldTransformations];
    newTransformer.options = { ...this.options, ...options };
    return newTransformer;
  }

  /**
   * Mark this transformer as strict (throw errors on missing required fields)
   */
  strict(): this {
    return this.withOptions({ strict: true });
  }

  /**
   * Configure to skip invalid items in arrays
   */
  skipInvalid(): this {
    return this.withOptions({ skipInvalid: true });
  }
}